import { useCart } from '@hooks/useCart/useCart';
import { useUpsertCharacteristics } from '@hooks/useUpsertCharacteristics/useUpsertCharacteristics';
import { useSubmitCart } from '@hooks/useSubmitCart/useSubmitCart';
import { useSelectPlanActions } from './useSelectPlanActions';
import { useAddOnActions } from './useAddOnActions';
import { useCouponActions } from './useCouponActions';

export { deriveCartSummary } from '@utils/cartPricing';
export type { CartSummary } from '@utils/cartPricing';

interface UseShoppingCartOptions {
  onError?: (error: Error) => void;
  onPlanSelected?: () => void;
}

export const useShoppingCart = (sessionId: string, { onError, onPlanSelected }: UseShoppingCartOptions = {}) => {
  const cartQuery = useCart(sessionId);
  const planActions = useSelectPlanActions(sessionId, { onError, onPlanSelected });
  const addOnActions = useAddOnActions(sessionId, { onError });
  const couponActions = useCouponActions(sessionId, { onError });

  // Dead surface kept for the unchanged Phase 0 contract — see the slice-06
  // deprecation inventory. No production consumer reaches these two mutations.
  const upsertCharM = useUpsertCharacteristics(sessionId, { onError });
  const submitM = useSubmitCart(sessionId, { onError });

  const isPending =
    planActions.isPending || addOnActions.isPending || couponActions.isApplyingCoupon || couponActions.isRemovingCoupon;

  return {
    cart: cartQuery.data ?? null,
    isCartLoading: cartQuery.isLoading,
    isCartError: cartQuery.isError,
    isPending,
    pendingPlanId: planActions.pendingPlanId,
    appliedCoupon: couponActions.appliedCoupon,
    isApplyingCoupon: couponActions.isApplyingCoupon,
    isRemovingCoupon: couponActions.isRemovingCoupon,
    applyCoupon: couponActions.applyCoupon,
    applyCouponAsync: couponActions.applyCouponAsync,
    removeCoupon: couponActions.removeCoupon,
    removeCouponAsync: couponActions.removeCouponAsync,

    selectPlan: planActions.selectPlan,
    selectPlanAsync: planActions.selectPlanAsync,
    selectAddOn: addOnActions.selectAddOn,
    selectAddOnAsync: addOnActions.selectAddOnAsync,
    selectAddOnsAsync: addOnActions.selectAddOnsAsync,
    removeAddOn: addOnActions.removeAddOn,
    removeAddOnAsync: addOnActions.removeAddOnAsync,
    removeAddOnsAsync: addOnActions.removeAddOnsAsync,
    removePlan: planActions.removePlan,
    removePlanAsync: planActions.removePlanAsync,
    removeAllItems: planActions.removeAllItems,

    addTopOffer: planActions.addTopOffer,
    addTopOfferAsync: planActions.addTopOfferAsync,
    removeTopOffer: planActions.removeTopOffer,
    addChildItemAsync: addOnActions.addChildItemAsync,
    updateCharacteristics: upsertCharM.mutate,

    submit: submitM.mutate,
    isSubmitting: submitM.isPending,
  };
};
